import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { HiArrowsUpDown, HiChevronUp, HiChevronDown, HiCloudArrowUp, HiMagnifyingGlass, HiArchiveBox, HiEllipsisVertical, HiEye } from 'react-icons/hi2';
import { StorageDirectory, StorageItem } from '../../../types/storage';
import { RootState } from '../../../redux/reducers';
import FilePreviewModal from './FilePreviewModal';
import {
  formatFileSize,
  formatDate,
  getFileIcon,
  getFolderIcon,
  sortItems,
  sortDirectories,
  filterItemsBySearch,
  filterDirectoriesBySearch,
} from '../../../utils/storageUtils';
import {
  setCurrentDirectory,
  toggleItemSelection,
  toggleDirectorySelection,
  selectAllItems,
  clearSelection,
  setSortBy,
  setSortOrder,
  updateItemData,
  updateDirectoryData,
  cancelRename,
} from '../../../redux/slices/storageSlice';
import storageService from '../../../services/community/storageService';

interface FileListViewProps {
  directories: StorageDirectory[];
  items: StorageItem[];
  selectedItemIds: number[];
  selectedDirectoryIds: number[];
  sortBy: 'name' | 'date' | 'size';
  sortOrder: 'asc' | 'desc';
  searchQuery: string;
  currentDirectoryId: number | null;
  onUpload: () => void;
  onContextMenu: (
    e: React.MouseEvent,
    item: StorageItem | null,
    directory: StorageDirectory | null
  ) => void;
}

function SortIcon({ column, sortBy, sortOrder }: { column: string; sortBy: string; sortOrder: string }) {
  if (sortBy !== column) {
    return <HiArrowsUpDown className="w-4 h-4 text-primary-900/30" />;
  }
  return sortOrder === 'asc' ? (
    <HiChevronUp className="w-4 h-4 text-primary" />
  ) : (
    <HiChevronDown className="w-4 h-4 text-primary" />
  );
}

export default function FileListView({
  directories,
  items,
  selectedItemIds,
  selectedDirectoryIds,
  sortBy,
  sortOrder,
  searchQuery,
  currentDirectoryId,
  onUpload,
  onContextMenu,
}: FileListViewProps): JSX.Element {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { renamingItemId, renamingDirectoryId, allDirectories: allDirs } = useSelector((state: RootState) => state.storage);
  const [editValue, setEditValue] = useState('');
  const [previewItem, setPreviewItem] = useState<StorageItem | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<number | null>(null);
  const dragCountersRef = useRef<Record<number, number>>({});

  const isDescendantOf = (dirId: number, candidateAncestorId: number): boolean => {
    let current: StorageDirectory | undefined = allDirs.find((d) => d.id === dirId);
    while (current) {
      if (current.id === candidateAncestorId) return true;
      const { parentId } = current;
      if (parentId === null) break;
      current = allDirs.find((d) => d.id === parentId);
    }
    return false;
  };

  const handleItemDragStart = (e: React.DragEvent, itemId: number) => {
    e.dataTransfer.setData('application/veraconnect-item', JSON.stringify({ id: itemId, type: 'item' }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDirectoryDragStart = (e: React.DragEvent, dirId: number) => {
    e.dataTransfer.setData('application/veraconnect-item', JSON.stringify({ id: dirId, type: 'directory' }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleFolderDragEnter = (e: React.DragEvent, targetDirId: number) => {
    e.preventDefault();
    if (!e.dataTransfer.types.includes('application/veraconnect-item')) return;
    dragCountersRef.current[targetDirId] = (dragCountersRef.current[targetDirId] ?? 0) + 1;
    setDragOverFolderId(targetDirId);
  };

  const handleFolderDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleFolderDragLeave = (e: React.DragEvent, targetDirId: number) => {
    dragCountersRef.current[targetDirId] = (dragCountersRef.current[targetDirId] ?? 1) - 1;
    if ((dragCountersRef.current[targetDirId] ?? 0) <= 0) {
      dragCountersRef.current[targetDirId] = 0;
      setDragOverFolderId((prev) => (prev === targetDirId ? null : prev));
    }
  };

  const handleFolderDrop = async (e: React.DragEvent, targetDir: StorageDirectory) => {
    e.preventDefault();
    e.stopPropagation();
    dragCountersRef.current[targetDir.id] = 0;
    setDragOverFolderId(null);

    const raw = e.dataTransfer.getData('application/veraconnect-item');
    if (!raw) return;

    let dragData: { id: number; type: 'item' | 'directory' };
    try {
      dragData = JSON.parse(raw);
    } catch {
      return;
    }

    if (dragData.type === 'item') {
      const item = items.find((i) => i.id === dragData.id);
      // Skip if already in this folder
      if (!item || item.directoryId === targetDir.id) return;
      try {
        const updated = await storageService.moveItem(dragData.id, { newDirectoryId: targetDir.id });
        dispatch(updateItemData(updated));
      } catch (err) {
        console.error('Failed to move item:', err);
      }
    } else if (dragData.type === 'directory') {
      // Don't drop a folder onto itself or a descendant
      if (dragData.id === targetDir.id) return;
      if (isDescendantOf(targetDir.id, dragData.id)) return;
      const dir = allDirs.find((d) => d.id === dragData.id);
      if (!dir || dir.parentId === targetDir.id) return;
      try {
        const updated = await storageService.moveDirectory(dragData.id, { newParentId: targetDir.id });
        dispatch(updateDirectoryData(updated));
      } catch (err) {
        console.error('Failed to move directory:', err);
      }
    }
  };

  // Filter and sort
  const filteredDirs = sortDirectories(
    filterDirectoriesBySearch(
      directories.filter((d) => d.parentId === currentDirectoryId),
      searchQuery
    ),
    sortBy,
    sortOrder
  );

  const filteredItems = sortItems(
    filterItemsBySearch(
      items.filter((i) => i.directoryId === currentDirectoryId),
      searchQuery
    ),
    sortBy,
    sortOrder
  );

  const hasItems = filteredDirs.length > 0 || filteredItems.length > 0;
  const allSelected =
    selectedItemIds.length === filteredItems.length && filteredItems.length > 0;

  const handleSort = (newSortBy: 'name' | 'date' | 'size') => {
    if (sortBy === newSortBy) {
      dispatch(setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'));
    } else {
      dispatch(setSortBy(newSortBy));
      dispatch(setSortOrder('asc'));
    }
  };

  const handleSelectAll = () => {
    if (allSelected) {
      dispatch(clearSelection());
    } else {
      dispatch(selectAllItems());
    }
  };

  const handleNavigateToDirectory = (dirId: number) => {
    dispatch(setCurrentDirectory(dirId));
  };

  const saveEdit = async (id: number, isDirectory: boolean) => {
    if (!editValue.trim()) {
      cancelEdit();
      return;
    }

    try {
      if (isDirectory) {
        const updated = await storageService.updateDirectory(id, { name: editValue.trim() });
        dispatch(updateDirectoryData(updated));
      } else {
        const updated = await storageService.updateItem(id, { fileName: editValue.trim() });
        dispatch(updateItemData(updated));
      }
      cancelEdit();
    } catch (err) {
      console.error('Failed to rename:', err);
      cancelEdit();
    }
  };

  const cancelEdit = () => {
    dispatch(cancelRename());
    setEditValue('');
  };

  // Pre-populate edit value when rename is triggered via context menu
  React.useEffect(() => {
    if (renamingItemId !== null) {
      const item = items.find((i) => i.id === renamingItemId);
      if (item) setEditValue(item.fileName);
    } else if (renamingDirectoryId !== null) {
      const dir = directories.find((d) => d.id === renamingDirectoryId);
      if (dir) setEditValue(dir.name);
    }
  }, [renamingItemId, renamingDirectoryId]);

  if (!hasItems && searchQuery) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-primary-900/50">
        <HiMagnifyingGlass className="w-20 h-20 mb-4 text-primary-100" />
        <p className="text-xl font-semibold text-primary-900 mb-1">{t('page.files.fileListView.noResultsTitle')}</p>
        <p className="text-sm text-primary-900/50">{t('page.files.fileListView.noResultsSubtitle')}</p>
      </div>
    );
  }

  if (!hasItems) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-primary-900/50">
        <HiArchiveBox className="w-20 h-20 mb-4 text-primary-100" />
        <p className="text-xl font-semibold text-primary-900 mb-1">{t('page.files.fileListView.emptyFolderTitle')}</p>
        <p className="text-sm text-primary-900/50 mb-6">{t('page.files.fileListView.emptyFolderSubtitle')}</p>
        <button
          type="button"
          onClick={onUpload}
          className="px-6 py-2.5 bg-primary text-white rounded-full hover:bg-primary-900 text-sm font-semibold transition-all hover:bg-opacity-80 flex items-center gap-2"
        >
          <HiCloudArrowUp className="w-5 h-5" />
          {t('page.files.fileListView.uploadFiles')}
        </button>
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden rounded-[8px]">
      <table className="min-w-full">
        <thead className="bg-primary-100">
          <tr>
            <th scope="col" className="w-12 px-6 py-3">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={handleSelectAll}
                className="rounded border-secondary-200 text-primary focus:ring-primary"
              />
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-semibold tracking-wider text-primary-900 cursor-pointer hover:text-primary transition-colors"
              onClick={() => handleSort('name')}
            >
              <div className="flex items-center gap-2">
                <span>{t('page.files.fileListView.name')}</span>
                <SortIcon column="name" sortBy={sortBy} sortOrder={sortOrder} />
              </div>
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-semibold tracking-wider text-primary-900"
            >
              {t('page.files.fileListView.uploadedBy')}
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-semibold tracking-wider text-primary-900 cursor-pointer hover:text-primary transition-colors"
              onClick={() => handleSort('size')}
            >
              <div className="flex items-center gap-2">
                <span>{t('page.files.fileListView.size')}</span>
                <SortIcon column="size" sortBy={sortBy} sortOrder={sortOrder} />
              </div>
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-semibold tracking-wider text-primary-900 cursor-pointer hover:text-primary transition-colors"
              onClick={() => handleSort('date')}
            >
              <div className="flex items-center gap-2">
                <span>{t('page.files.fileListView.modified')}</span>
                <SortIcon column="date" sortBy={sortBy} sortOrder={sortOrder} />
              </div>
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold tracking-wider text-primary-900">
              {t('page.files.fileListView.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Directories */}
          {filteredDirs.map((dir) => (
            <tr
              key={`dir-${dir.id}`}
              draggable
              onDragStart={(e) => handleDirectoryDragStart(e, dir.id)}
              onDragEnter={(e) => handleFolderDragEnter(e, dir.id)}
              onDragOver={handleFolderDragOver}
              onDragLeave={(e) => handleFolderDragLeave(e, dir.id)}
              onDrop={(e) => handleFolderDrop(e, dir)}
              className={`group cursor-pointer transition-all ${
                dragOverFolderId === dir.id
                  ? 'bg-primary-100 ring-2 ring-inset ring-primary'
                  : selectedDirectoryIds.includes(dir.id)
                  ? 'bg-primary-100/60'
                  : 'bg-secondary-200/40 hover:bg-secondary-200/70'
              }`}
              onContextMenu={(e) => onContextMenu(e, null, dir)}
            >
              <td className="px-6 py-4">
                <input
                  type="checkbox"
                  checked={selectedDirectoryIds.includes(dir.id)}
                  onChange={() => dispatch(toggleDirectorySelection(dir.id))}
                  onClick={(e) => e.stopPropagation()}
                  className="rounded border-secondary-200 text-primary focus:ring-primary"
                />
              </td>
              <td
                className="px-4 py-4"
                onDoubleClick={() => handleNavigateToDirectory(dir.id)}
              >
                <div className="flex items-center gap-3">
                  {getFolderIcon()}
                  {renamingDirectoryId === dir.id ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => saveEdit(dir.id, true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit(dir.id, true);
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="px-3 py-1.5 border-2 border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                    />
                  ) : (
                    <span
                      className="font-semibold text-primary-900 hover:text-primary transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNavigateToDirectory(dir.id);
                      }}
                    >
                      {dir.name}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-4 text-sm text-primary-900/50">-</td>
              <td className="px-4 py-4 text-sm text-primary-900/50">-</td>
              <td className="px-4 py-4 text-sm text-primary-900/50">{formatDate(dir.updatedAt)}</td>
              <td className="px-4 py-4">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onContextMenu(e, null, dir);
                  }}
                  className="text-primary-900/30 hover:text-primary-900 transition-all"
                >
                  <HiEllipsisVertical className="w-5 h-5" />
                </button>
              </td>
            </tr>
          ))}

          {/* Files */}
          {filteredItems.map((item) => (
            <tr
              key={`item-${item.id}`}
              draggable
              onDragStart={(e) => handleItemDragStart(e, item.id)}
              className={`group transition-all ${
                selectedItemIds.includes(item.id)
                  ? 'bg-primary-100/60'
                  : 'bg-secondary-200/40 hover:bg-secondary-200/70'
              }`}
              onContextMenu={(e) => onContextMenu(e, item, null)}
            >
              <td className="px-6 py-4">
                <input
                  type="checkbox"
                  checked={selectedItemIds.includes(item.id)}
                  onChange={() => dispatch(toggleItemSelection(item.id))}
                  onClick={(e) => e.stopPropagation()}
                  className="rounded border-secondary-200 text-primary focus:ring-primary"
                />
              </td>
              <td className="px-4 py-4">
                <div className="flex items-center gap-3">
                  {getFileIcon(item.fileName)}
                  {renamingItemId === item.id ? (
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={() => saveEdit(item.id, false)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEdit(item.id, false);
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="px-3 py-1.5 border-2 border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                    />
                  ) : (
                    <button
                      type="button"
                      className="text-primary-900 hover:text-primary font-medium transition-colors flex items-center gap-1.5 group/name"
                      onClick={(e) => { e.stopPropagation(); setPreviewItem(item); }}
                    >
                      {item.fileName}
                      <HiEye className="w-3.5 h-3.5 opacity-0 group-hover/name:opacity-60 transition-opacity" />
                    </button>
                  )}
                </div>
              </td>
              <td className="px-4 py-4 text-sm text-primary-900/60">{item.uploader ? `${item.uploader.firstName} ${item.uploader.lastName}` : '-'}</td>
              <td className="px-4 py-4 text-sm text-primary-900/50">{formatFileSize(item.fileSizeInBytes)}</td>
              <td className="px-4 py-4 text-sm text-primary-900/50">{formatDate(item.updatedAt)}</td>
              <td className="px-4 py-4">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onContextMenu(e, item, null);
                  }}
                  className="text-primary-900/30 hover:text-primary-900 transition-all"
                >
                  <HiEllipsisVertical className="w-5 h-5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <FilePreviewModal item={previewItem} onClose={() => setPreviewItem(null)} />
    </div>
  );
}
